import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  Outlet,
  useMatchRoute,
} from "@tanstack/react-router";
import clsx from "clsx";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { Dialog } from "@/components/Dialog/Dialog";
import { BookingError } from "@/components/Errors/BookingError";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { OfferNotAvailableError } from "@/components/Errors/OfferNotAvailableError";
import { IconSadFace } from "@/components/Icons";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import { TripType } from "@/features/flights/types/types";
import { TravellersSelectionProvider } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { useRouteConfig } from "@/hooks/useRouteConfig";
import { type OfferSelectedEventData } from "@/types/types";
import { isOfferIsNotAvailableAnymoreError } from "@/utils/assertions";

export const Route = createFileRoute("/_authenticated/flights/_offers/offer")({
  component: RouteComponent,
  errorComponent: (props: ErrorComponentProps) => {
    Sentry.captureException(props.error);

    if (isOfferIsNotAvailableAnymoreError(props.error)) {
      return <OfferNotAvailableError variant="flight" />;
    }

    return <BookingError {...props} type="flights" />;
  },
  onEnter: () => {
    const event = new CustomEvent<OfferSelectedEventData>("offerselected", {
      detail: {
        offerType: "air",
      },
    });
    window.dispatchEvent(event);
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const routeConfig = useRouteConfig();
  const intl = useIntl();

  const { selectedFlightPayload } = useFlightSelectionPayloadContext();

  const { searchData } = useFlightSearchDataContext();
  const { purchaseState } = usePurchaseStateContext();
  useQuery(travellersQueryOptions()); // for preloading
  const { error } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );

  const matches = useMatchRoute();

  const isTicketPurchasedRoute = matches({
    to: "/flights/offer/$id/confirmation",
    from: "/",
  });

  const initialTravellersSlots = useMemo(() => {
    if (!searchData) return;

    return {
      ADT: Array.from({ length: searchData.adult }, () => null),
      CHD: Array.from({ length: searchData.child }, () => null),
      INF: Array.from({ length: searchData.infantWithoutSeat }, () => null),
    };
  }, [searchData]);

  if (!isTicketPurchasedRoute && purchaseState === "completed") {
    return <Navigate to="/" />;
  }

  return (
    <TravellersSelectionProvider
      key={`${initialTravellersSlots?.ADT.length}-${initialTravellersSlots?.CHD.length}-${initialTravellersSlots?.INF.length}`}
      initialTravellersSlots={initialTravellersSlots}
      travelDate={new Date(searchData!.dates.departure)}
    >
      <main
        className={clsx("no-footer main flex flex-col", {
          "with-drawer": !!routeConfig.drawer,
        })}
      >
        <div className="mt-2 mb-4 flex min-h-0 w-full flex-1 flex-col">
          <Outlet />

          <Dialog isOpen={isOfferIsNotAvailableAnymoreError(error)}>
            <ErrorPanel
              icon={IconSadFace}
              primaryText={intl.formatMessage({
                defaultMessage: "This flight offer is not available anymore",
                description:
                  "Error heading when the selected flight offer has expired",
              })}
              secondaryText={intl.formatMessage({
                defaultMessage:
                  "Unfortunately, this flight offer is not available anymore. Please try again later or choose another flight.",
                description:
                  "Explanation that the selected flight offer is no longer available",
              })}
            >
              <Button
                variant="outlined"
                borderRoundness="medium"
                color="primary"
                size="small"
                onClick={() => {
                  navigate({
                    to:
                      searchData?.tripType === TripType.ROUND_TRIP
                        ? "/flights/return/list"
                        : "/flights/departure/list",
                  });
                }}
              >
                {intl.formatMessage({
                  defaultMessage: "Back to search",
                  description:
                    "Button to return to search when the selected offer has expired or is unavailable",
                })}
              </Button>
            </ErrorPanel>
          </Dialog>

          {!isTicketPurchasedRoute && <WhatsAppButton bottomPosition="20vh" />}
        </div>
      </main>
    </TravellersSelectionProvider>
  );
}
